'use client';

import { cn } from '@/lib/utils';
import { useState, useRef } from 'react';

interface AndroidProps {
  className?: string;
  videoSrc?: string;
  imageSrc?: string;
}

export function Android({ className, videoSrc, imageSrc }: AndroidProps) {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <div className={cn('relative mx-auto', className)}>
      {/* Android Phone Frame */}
      <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl">
        {/* Top speaker/camera area */}
        <div className="w-[148px] h-[18px] bg-gray-800 top-[18px] rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>

        {/* Screen area */}
        <div className="rounded-[2rem] overflow-hidden w-[272px] h-[572px] bg-white dark:bg-gray-900 relative">
          {videoSrc ? (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={videoSrc} type="video/mp4" />
              </video>

              {/* Volume Control Button */}
              <button
                onClick={toggleMute}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors z-10"
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              >
                {isMuted ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.824L4.618 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.618l3.765-2.824a1 1 0 011.617.824zM14.22 7.22a.75.75 0 011.06 0l1.5 1.5a.75.75 0 010 1.06l-1.5 1.5a.75.75 0 11-1.06-1.06L14.94 9.5l-.72-.72a.75.75 0 010-1.06z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 3.75a.75.75 0 00-1.264-.546L5.203 6H2.667a.75.75 0 00-.75.75v6.5c0 .414.336.75.75.75h2.536l3.533 2.796A.75.75 0 0010 16.25V3.75zM15.95 5.05a.75.75 0 011.06 0c2.067 2.067 2.067 5.42 0 7.486a.75.75 0 01-1.06-1.06 3.992 3.992 0 000-5.364.75.75 0 010-1.062z" />
                    <path d="M13.536 7.536a.75.75 0 011.06 0 1.993 1.993 0 010 2.828.75.75 0 01-1.06-1.06.493.493 0 000-.708.75.75 0 010-1.06z" />
                  </svg>
                )}
              </button>
            </>
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt="Android screen content"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <div className="text-gray-400 text-sm">Android Screen</div>
            </div>
          )}
        </div>

        {/* Home button */}
        <div className="rounded-full w-[35px] h-[35px] absolute -bottom-[17px] left-1/2 -translate-x-1/2 bg-gray-800"></div>
      </div>
    </div>
  );
}

export default Android;